import React, { useCallback, useEffect } from 'react'
import {
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Box,
  useTheme,
  IconButton,
  Divider,
} from '@mui/material'

import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  // LineChart,
  // Line,
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
} from 'recharts'
import useCustomStyles from '../../../../../utils/hooks/CustomStylesHook'
import {
  API_VERSION_V3,
  DATE_DIRECTIONS,
  getIconColor,
  getStartAndEndDate,
  GRAPH_PERIODS,
  safeRangeKeys,
  VITAL_KEYS,
} from '../Constant'
import dayjs from 'dayjs'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { getVitalTracking } from '../../../../../services/DeviceServices'
import { useAppSelector } from '../../../../../app/hooks'
import { selectPatientData } from '../../../OpdPatientSlice'
import { selectSafeRanges } from '../DeviceSlice'
import CommonBarGraph from './CommonBarGraph'

const styles = (theme: any) => ({
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  toggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },

  toggleBtn: {
    fontSize: '14px',
    fontWeight: 600,
    minWidth: '60px',
  },
  dateDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },

  card: {
    backgroundColor: '#2c2c2c', // dark background
    color: '#fff',
    padding: theme.spacing(1),
    borderRadius: 12,
    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
    minWidth: 200,
  },
  title: {
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    marginRight: theme.spacing(1),
  },
  greenDot: {
    backgroundColor: 'green',
  },
  redDot: {
    backgroundColor: 'red',
  },
})

const HeartRateTracking: React.FC = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  const [period, setPeriod] = React.useState(GRAPH_PERIODS.DAY)
  const [currentDate, setCurrentDate] = React.useState(dayjs())
  const [chartData, setChartData] = React.useState([])
  const [avgData, setAvgData] = React.useState<any>({})

  const { startDate, endDate } = React.useMemo(() => {
    return getStartAndEndDate(currentDate, period)
  }, [currentDate, period])

  const patient = useAppSelector(selectPatientData)
  const safeRanges = useAppSelector(selectSafeRanges)

  const handlePeriodChange = (
    event: React.MouseEvent<HTMLElement>,
    newPeriod: string | null
  ) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod)
    }
  }

  const changeDate = (direction: string) => {
    const amount =
      period === GRAPH_PERIODS?.DAY
        ? 1
        : period === GRAPH_PERIODS?.WEEK
        ? 7
        : 30
    setCurrentDate((prev) =>
      prev.add(direction === DATE_DIRECTIONS?.NEXT ? amount : -amount, 'day')
    )
  }

  const handleGetHeartRate = useCallback(async () => {
    const response = await getVitalTracking(
      patient?.muhId,
      VITAL_KEYS?.HEART_RATE,
      API_VERSION_V3,
      startDate.format('YYYY-MM-DD'),
      endDate.format('YYYY-MM-DD'),
      period.toUpperCase()
    )
    if (response?.success) {
      setChartData(
        period === GRAPH_PERIODS?.DAY
          ? response?.data?.heartRates
          : response?.data?.dailyRanges
      )
      setAvgData({
        average:
          period === GRAPH_PERIODS?.DAY
            ? response?.data?.averageHeartRate?.toFixed(0)
            : response?.data?.overallAvg?.toFixed(0),
        minimum:
          period === GRAPH_PERIODS?.DAY
            ? response?.data?.minHeartRate?.toFixed(0)
            : response?.data?.overallMin?.toFixed(0),
        maximum:
          period === GRAPH_PERIODS?.DAY
            ? response?.data?.maxHeartRate?.toFixed(0)
            : response?.data?.overallMax?.toFixed(0),
      })
    }
  }, [patient?.muhId, startDate, endDate, period])

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
      const color = getIconColor(
        safeRanges,
        safeRangeKeys?.HEARTRATE,
        parseInt(payload?.[0]?.value)
      )

      return (
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'start'}
          bgcolor='white'
          padding={2}
          border='1px solid grey'
          gap={1.3}
          borderRadius={5}
        >
          <Typography fontSize={'12px'} fontWeight={600}>
            {label}
          </Typography>
          <Typography display='flex' alignItems='center' gap={0.5}>
            <Box
              component='span'
              color={color}
              fontSize='1.2rem'
              lineHeight={1}
            >
              ●
            </Box>
            <Box component='span' fontSize={'12px'}>
              Heart Rate - {payload?.[0]?.value}
            </Box>
          </Typography>
          <Divider flexItem />
          <Typography fontSize={'12px'} fontWeight={500}>
            Min - {payload?.[0]?.payload?.minValue}@
            {payload?.[0]?.payload?.minTime}
          </Typography>
          <Typography fontSize={'12px'} fontWeight={500}>
            Max - {payload?.[0]?.payload?.maxValue}@
            {payload?.[0]?.payload?.maxTime}
          </Typography>
        </Box>
      )
    }
    return null
  }

  useEffect(() => {
    handleGetHeartRate()
  }, [handleGetHeartRate])

  return (
    <Box m={3}>
      <Box className={classes?.toggleGroup}>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          color='primary'
          size='small'
        >
          <ToggleButton value='day' className={classes?.toggleBtn}>
            D
          </ToggleButton>
          <ToggleButton value='week' className={classes?.toggleBtn}>
            W
          </ToggleButton>
          <ToggleButton value='month' className={classes?.toggleBtn}>
            M
          </ToggleButton>
        </ToggleButtonGroup>
        <Box className={classes?.dateDisplay}>
          <IconButton
            onClick={() => changeDate(DATE_DIRECTIONS?.PREVIOUS)}
            color='inherit'
            aria-label='chevron left'
          >
            <ChevronLeft />
          </IconButton>
          <Typography>
            {currentDate.format(
              period === GRAPH_PERIODS?.DAY
                ? 'MMM DD, YYYY'
                : period === GRAPH_PERIODS?.WEEK
                ? '[Week of] MMM DD, YYYY'
                : 'MMM YYYY'
            )}
          </Typography>
          <IconButton
            onClick={() => changeDate(DATE_DIRECTIONS?.NEXT)}
            color='inherit'
            aria-label='chevron right'
            disabled={
              currentDate.isSame(dayjs(), 'day') ||
              currentDate.isAfter(dayjs(), 'day')
            }
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {period === GRAPH_PERIODS?.DAY && (
        <Box display={'flex'} justifyContent={'center'} mb={4}>
          <Box className={classes.card}>
            <Typography variant='subtitle1' className={classes.title}>
              Heart Rate Levels
            </Typography>

            <Box className={classes.row}>
              <Box className={`${classes.dot} ${classes.greenDot}`} />
              <Typography fontSize={'12px'} variant='body2'>
                {safeRanges?.[safeRangeKeys?.HEARTRATE]?.min} -{' '}
                {safeRanges?.[safeRangeKeys?.HEARTRATE]?.max} : Normal
              </Typography>
            </Box>

            <Box className={classes.row}>
              <Box className={`${classes.dot} ${classes.redDot}`} />
              <Typography fontSize={'12px'} variant='body2'>
                Above {safeRanges?.[safeRangeKeys?.HEARTRATE]?.max} bpm: High
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Grid container spacing={2} direction={'column'}>
        <Typography fontSize={'13px'} fontWeight={600} ml={4} mb={0.5}>
          BPM
        </Typography>
        {period === GRAPH_PERIODS?.DAY ? (
          <ResponsiveContainer width='100%' height={210}>
            <BarChart data={chartData} margin={{ bottom: 40 }}>
              <XAxis
                dataKey='time'
                fontSize={'12px'}
                interval={0}
                angle={-90}
                textAnchor='end'
                tick={{ fontSize: 12 }}
              />
              <CartesianGrid strokeDasharray='3 3' />
              <YAxis domain={[0, 'dataMax']} fontSize='12px' />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey='value'
                strokeWidth={1.5}
                fill='red'
                barSize={6}
                radius={[4, 4, 0, 0]}
              >
                {chartData?.map((entry: any, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getIconColor(
                      safeRanges,
                      safeRangeKeys?.HEARTRATE,
                      parseInt(entry?.value)
                    )}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <CommonBarGraph chartData={chartData} />
        )}
        <Box
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          gap={3}
          mt={1.5}
          ml={4}
        >
          <Grid
            item
            xs={6}
            textAlign={'center'}
            border={'1px solid grey'}
            borderRadius={'20px'}
            p={2}
          >
            <Typography variant='h6'>{avgData?.average}</Typography>
            <Typography variant='subtitle2' fontWeight={600}>
              Avg
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            textAlign={'center'}
            border={'1px solid grey'}
            borderRadius={'20px'}
            p={2}
          >
            <Typography variant='h6'>{avgData?.minimum}</Typography>
            <Typography variant='subtitle2' fontWeight={600}>
              Minimum
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            textAlign={'center'}
            border={'1px solid grey'}
            borderRadius={'20px'}
            p={2}
          >
            <Typography variant='h6'>{avgData?.maximum}</Typography>
            <Typography variant='subtitle2' fontWeight={600}>
              Maximum
            </Typography>
          </Grid>
        </Box>
      </Grid>
    </Box>
  )
}

export default HeartRateTracking
